/**
 * The guide's light rig.
 *
 * IWSDK creates a world with **no lights and no environment map**. It registers
 * the light components and an `EnvironmentSystem`, but neither does anything
 * until something adds a component — so a PBR model dropped into passthrough is
 * lit by nothing and renders pure black. Nothing in the SDK warns about this;
 * the model simply arrives as a silhouette.
 *
 * The rig is aimed from the guide's facing rather than fixed to the room. The
 * guide yaws to face the viewer, so a room-fixed key would swing round behind
 * its head as the user walks about; tracking the facing keeps the same
 * three-quarter light on the face from every angle.
 */
import {
  DirectionalLightComponent,
  HemisphereLightComponent,
  IBLTexture,
  LevelRoot,
  type Entity,
  type World,
} from '@iwsdk/core';
import { Object3D, Quaternion, Vector3 } from 'three';

import { uiLog } from '../ui/uiLog.js';
import type { AvatarLighting, DirectionalLightSpec } from './profile.js';

/** Three's directional lights emit along local -Z. */
const EMIT_AXIS = new Vector3(0, 0, -1);

export interface LightRig {
  /**
   * Aim the rig along the guide's facing. Pass the model's **world**
   * quaternion every frame.
   */
  setFacing(facing: Quaternion): void;
  dispose(): void;
}

/**
 * Build the rig and return the handle that keeps it aimed.
 *
 * The lights sit at the level root rather than under the guide. Three skips
 * invisible subtrees when it gathers lights, and the guide is deliberately
 * hidden until the user places it — parenting the rig there would make the
 * lighting depend on the very thing it lights. Aiming is explicit anyway, so
 * inheriting the guide's transform buys nothing.
 *
 * Every piece is independent: a light whose intensity is zero is simply never
 * created, and IBL failing does not stop the directional lights from working.
 */
export function installLighting(world: World, lighting: AvatarLighting): LightRig {
  const entities: Entity[] = [];
  const aimed: { object: Object3D; offset: Quaternion }[] = [];

  const addDirectional = (name: string, spec: DirectionalLightSpec): void => {
    if (spec.intensity <= 0) return;
    const object = new Object3D();
    object.name = name;
    const entity = world.createTransformEntity(object);
    entity.addComponent(DirectionalLightComponent, {
      color: srgb(spec.color),
      intensity: spec.intensity,
    });
    entities.push(entity);
    aimed.push({ object, offset: aimQuaternion(spec.direction) });
  };

  addDirectional('guide-key-light', lighting.key);
  addDirectional('guide-fill-light', lighting.fill);

  if (lighting.ambient.intensity > 0) {
    // Hemisphere lights are world-oriented by definition — the binding forces
    // world up and identity rotation — so this one never needs aiming.
    const object = new Object3D();
    object.name = 'guide-ambient-light';
    const entity = world.createTransformEntity(object);
    entity.addComponent(HemisphereLightComponent, {
      skyColor: srgb(lighting.ambient.skyColor),
      groundColor: srgb(lighting.ambient.groundColor),
      intensity: lighting.ambient.intensity,
    });
    entities.push(entity);
  }

  installEnvironment(world, lighting);

  const composed = new Quaternion();
  return {
    setFacing(facing) {
      for (const { object, offset } of aimed) {
        object.quaternion.copy(composed.copy(facing).multiply(offset));
      }
    },
    dispose() {
      for (const entity of entities) entity.destroy();
      entities.length = 0;
      aimed.length = 0;
    },
  };
}

/**
 * Image-based lighting, via IWSDK's own `EnvironmentSystem`.
 *
 * That system only looks at entities carrying `LevelRoot`, and the default
 * level root created during world init never receives it — `LevelSystem` adds
 * the marker only on the root it creates itself, which is not this one. So the
 * marker is added here, or the IBL component would sit on an entity nothing
 * queries and quietly do nothing.
 */
function installEnvironment(world: World, lighting: AvatarLighting): void {
  if (lighting.environment === 'none' || lighting.environmentIntensity <= 0) return;

  const root = world.activeLevel?.value;
  if (!root) {
    uiLog.push('lighting: no active level — image-based lighting skipped');
    return;
  }

  try {
    if (!root.hasComponent(LevelRoot)) root.addComponent(LevelRoot);
    root.addComponent(IBLTexture, {
      src: lighting.environment,
      intensity: lighting.environmentIntensity,
    });
  } catch (error) {
    // Lights alone still illuminate the guide; only reflections are lost.
    uiLog.push(`lighting: environment "${lighting.environment}" failed — ${describe(error)}`);
  }
}

/** Local rotation that makes a light emit along `direction`. Exported for tests. */
export function aimQuaternion(direction: [number, number, number]): Quaternion {
  const target = new Vector3(...direction).normalize();
  return new Quaternion().setFromUnitVectors(EMIT_AXIS, target);
}

/**
 * `#rrggbb` to the `[r, g, b, a]` the light components expect.
 *
 * Deliberately not `THREE.Color`: the binding calls `setRGB(…, SRGBColorSpace)`
 * on these values, so they must stay raw sRGB. `Color` would convert to linear
 * on the way in and the conversion would then be applied twice, washing every
 * light out.
 *
 * Exported for tests.
 */
export function srgb(value: string): [number, number, number, number] {
  const digits =
    value.length === 4
      ? value
          .slice(1)
          .split('')
          .map((c) => c + c)
          .join('')
      : value.slice(1);
  const packed = Number.parseInt(digits, 16);
  return [((packed >> 16) & 255) / 255, ((packed >> 8) & 255) / 255, (packed & 255) / 255, 1];
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
